// src/redux/slices/statuses.js
import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
} from "@reduxjs/toolkit";
import { apiRequest } from "../../utils/api";

// --- auth header (JWT from localStorage) ---
const authHeader = () => {
  const t = localStorage.getItem("access");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Small helper for UI fallback
const humanize = (s = "") =>
  String(s)
    .split("_")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");

// Ensure every record has a unique, stable id + sane defaults
const normalizeStatus = (s, idx) => {
  const safeId =
    s?.id ??
    (s?.code ? `code:${s.code}` : `row:${idx}`); // <- guarantees uniqueness even if id missing
  return {
    id: safeId,
    code: s?.code ?? `auto_${idx}`,
    label: s?.label ?? humanize(s?.code ?? ""),
    target: s?.target ?? "both",
    color: s?.color ?? "#1677ff",
    is_active: s?.is_active ?? true,
    sort_order: Number.isFinite(s?.sort_order) ? s.sort_order : 0,
    email_template: s?.email_template ?? "",
  };
};

// --- entity adapter (store by id OR code as fallback) ---
const statusesAdapter = createEntityAdapter({
  selectId: (s) => s.id ?? s.code, // ✅ no collisions when backend lacks id
  sortComparer: (a, b) => {
    const ao = Number.isFinite(a.sort_order) ? a.sort_order : 0;
    const bo = Number.isFinite(b.sort_order) ? b.sort_order : 0;
    if (ao !== bo) return ao - bo;
    return String(a.label || "").localeCompare(String(b.label || ""));
  },
});

const initialState = statusesAdapter.getInitialState({
  loading: false,
  error: null,
  lastFetchedAt: null,
});

const STALE_MS = 5 * 60 * 1000; // 5 minutes

// ---------------------------------------------
// Fetch all (optionally include inactive)
// ---------------------------------------------
export const fetchStatuses = createAsyncThunk(
  "statuses/fetchAll",
  async (params = {}, thunkAPI) => {
    const { includeInactive = false, ordering } = params;
    const q = new URLSearchParams();
    if (includeInactive) q.set("include_inactive", "1");
    if (ordering) q.set("ordering", ordering);
    const url = `/statuses/${q.toString() ? `?${q.toString()}` : ""}`;
    const res = await apiRequest(url, { headers: { ...authHeader() } });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        if (j?.detail) msg = j.detail;
      } catch {}
      throw new Error(msg);
    }
    const data = await res.json();
    const raw = Array.isArray(data) ? data : data.results ?? [];
    // 🔧 normalize here
    return raw.map(normalizeStatus);
  },
  {
    condition: (params = {}, { getState }) => {
      if (params.force) return true;
      const { statuses } = getState();
      if (!statuses?.lastFetchedAt) return true;
      return Date.now() - statuses.lastFetchedAt > STALE_MS;
    },
  }
);

// ---------------------------------------------
// Create
// ---------------------------------------------
export const createStatus = createAsyncThunk(
  "statuses/create",
  async (payload, thunkAPI) => {
    const res = await apiRequest(`/statuses/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        msg = j?.detail || j?.error || JSON.stringify(j) || msg;
      } catch {}
      throw new Error(msg);
    }
    const created = await res.json();
    return normalizeStatus(created, 0); // normalize new record
  }
);

// ---------------------------------------------
// Update (PATCH)
// ---------------------------------------------
export const updateStatus = createAsyncThunk(
  "statuses/update",
  async ({ id, data }, thunkAPI) => {
    if (!id) throw new Error("Missing id");
    const res = await apiRequest(`/statuses/${id}/`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...authHeader(),
      },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        msg = j?.detail || j?.error || JSON.stringify(j) || msg;
      } catch {}
      throw new Error(msg);
    }
    const updated = await res.json();
    return normalizeStatus(updated, 0);
  }
);

// ---------------------------------------------
// Delete
// ---------------------------------------------
export const deleteStatus = createAsyncThunk(
  "statuses/delete",
  async (id, thunkAPI) => {
    if (!id) throw new Error("Missing id");
    const res = await apiRequest(`/statuses/${id}/`, {
      method: "DELETE",
      headers: { ...authHeader() },
    });
    if (!res.ok && res.status !== 204) {
      let msg = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        msg = j?.detail || j?.error || JSON.stringify(j) || msg;
      } catch {}
      throw new Error(msg);
    }
    return { id };
  }
);

// ---------------------------------------------
// Slice
// ---------------------------------------------
const statusesSlice = createSlice({
  name: "statuses",
  initialState,
  reducers: {
    invalidate(state) {
      state.lastFetchedAt = null;
    },
    upsertOne: statusesAdapter.upsertOne,
  },
  extraReducers: (b) => {
    // fetch
    b.addCase(fetchStatuses.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(fetchStatuses.fulfilled, (s, a) => {
      s.loading = false;
      statusesAdapter.setAll(s, a.payload);
      s.lastFetchedAt = Date.now();
    });
    b.addCase(fetchStatuses.rejected, (s, a) => {
      s.loading = false;
      s.error = a.error?.message || "Failed to load statuses";
    });

    // create
    b.addCase(createStatus.fulfilled, (s, a) => {
      statusesAdapter.addOne(s, a.payload);
    });

    // update
    b.addCase(updateStatus.fulfilled, (s, a) => {
      statusesAdapter.upsertOne(s, a.payload);
    });

    // delete
    b.addCase(deleteStatus.fulfilled, (s, a) => {
      statusesAdapter.removeOne(s, a.payload.id);
    });
  },
});

export const { invalidate, upsertOne } = statusesSlice.actions;
export default statusesSlice.reducer;

// ---------------------------------------------
// Selectors
// ---------------------------------------------
const baseSelectors = statusesAdapter.getSelectors((state) => state.statuses);

export const selectAllStatuses = baseSelectors.selectAll;
export const selectStatusById = baseSelectors.selectById;
export const selectStatusIds = baseSelectors.selectIds;

export const selectCrewStatuses = createSelector(selectAllStatuses, (all) =>
  all.filter((s) => s.target === "crew" || s.target === "both")
);
export const selectCompanyStatuses = createSelector(selectAllStatuses, (all) =>
  all.filter((s) => s.target === "company" || s.target === "both")
);

// Lookup by code when needed
export const makeSelectStatusByCode = (code) =>
  createSelector(selectAllStatuses, (all) => all.find((s) => s.code === code) || null);

// AntD-select friendly options (safe label fallback)
export const selectAllStatusOptions = createSelector(selectAllStatuses, (all) =>
  all.map((s) => ({ value: s.code, label: s.label || humanize(s.code) }))
);
export const selectCrewStatusOptions = createSelector(selectCrewStatuses, (list) =>
  list.map((s) => ({ value: s.code, label: s.label || humanize(s.code) }))
);
export const selectCompanyStatusOptions = createSelector(selectCompanyStatuses, (list) =>
  list.map((s) => ({ value: s.code, label: s.label || humanize(s.code) }))
);

// Loading / error
export const selectStatusesLoading = (state) => state.statuses.loading;
export const selectStatusesError = (state) => state.statuses.error;
export const selectStatusesLastFetchedAt = (state) => state.statuses.lastFetchedAt;
